import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Moon, LogOut } from "lucide-react";
import { Reveal } from "../components/motion";
import { useAuth } from "../context/AuthContext";

export default function Account() {
  const { user, loading, login, register, logout } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (loading) return <div className="bg-cream py-32 text-center text-navy/40">Loading…</div>;

  if (user) return (
    <div className="bg-cream" data-testid="account-page">
      <div className="mx-auto max-w-xl px-6 py-20">
        <Reveal>
          <div className="rounded-[2.5rem] bg-white p-10 text-center shadow-[0_16px_50px_rgba(16,42,86,0.08)]">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-lavender">
              <Moon className="text-lavdeep" size={28} />
            </span>
            <h1 className="mt-5 font-display text-3xl font-medium text-navy" data-testid="account-greeting">Hi, {user.name}</h1>
            <p className="mt-2 text-sm text-navy/50">{user.email}</p>
            {user.role === "admin" && (
              <Link to="/admin" data-testid="account-admin-link"
                className="mt-6 inline-block rounded-full bg-navy px-6 py-3 font-display text-sm font-semibold text-white transition hover:scale-[1.03]">
                OPEN ADMIN PANEL
              </Link>
            )}
            <div className="mt-8 rounded-2xl border border-dashed border-blush bg-cream p-6 text-sm text-navy/45">
              Order history will appear here after your first Buddy ships.
            </div>
            <button onClick={logout} data-testid="logout-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-blush px-6 py-3 font-display text-sm font-semibold text-navy/60 transition hover:border-pink hover:text-pinkdeep">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = mode === "login"
      ? await login(form.email, form.password)
      : await register(form.name, form.email, form.password);
    setBusy(false);
    if (res.ok) navigate("/account");
    else setError(res.error);
  };

  const inputCls = "w-full rounded-2xl border border-blush bg-cream px-5 py-3.5 text-sm text-navy placeholder:text-navy/35 focus:border-pink focus:outline-none";

  return (
    <div className="bg-cream" data-testid="auth-page">
      <div className="mx-auto max-w-md px-6 py-20">
        <Reveal>
          <div className="rounded-[2.5rem] bg-white p-9 shadow-[0_16px_50px_rgba(16,42,86,0.08)]">
            <img src="/assets/logo.png" alt="Bedtime Buddies" className="mx-auto h-14 w-auto" />
            <h1 className="mt-6 text-center font-display text-2xl font-medium text-navy">
              {mode === "login" ? "Welcome back to bedtime." : "Join the family."}
            </h1>
            <div className="mt-6 grid grid-cols-2 gap-2 rounded-full bg-blush/60 p-1.5">
              {["login", "register"].map((m) => (
                <button key={m} onClick={() => { setMode(m); setError(""); }} data-testid={`auth-tab-${m}`}
                  className={`rounded-full py-2.5 font-display text-sm font-semibold transition ${mode === m ? "bg-pink text-navy" : "text-navy/50 hover:text-navy"}`}>
                  {m === "login" ? "Sign In" : "Create Account"}
                </button>
              ))}
            </div>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
              {mode === "register" && (
                <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  aria-label="Name" data-testid="auth-name" className={inputCls} />
              )}
              <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                aria-label="Email" data-testid="auth-email" className={inputCls} />
              <input required type="password" placeholder="Password (min 6 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                aria-label="Password" data-testid="auth-password" className={inputCls} />
              {error && <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-500" data-testid="auth-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="auth-submit"
                className="w-full rounded-full bg-pink py-4 font-display font-semibold tracking-wide text-navy transition hover:bg-moon disabled:opacity-50">
                {busy ? "One moment…" : mode === "login" ? "SIGN IN" : "CREATE ACCOUNT"}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
